import { IdHelper } from '../helpers/id.helper';
import { WriteApiService } from '../services/write-api.service';
import { LogEventContexts, LogEvents, LogModel } from '../models';
import { Channel, NotificationTemplateType as TemplateType } from '../project-settings/notifications';

const DOCUMENT_NAME = 'logs';

class LogRepository {
  static getDocumentPath(eventContext: LogEventContexts, group: string, collectionPathBase: string[]): string[] {
    return [...collectionPathBase, DOCUMENT_NAME, eventContext, group.replace(/\./g, '_')];
  }

  /**
   * `paths` chega como [scope, 'logs', context, group], onde scope e o
   * projectId ou `core` para eventos de sessao. O autor nao e enviado: o
   * backend o resolve a partir do usuario autenticado.
   */
  static writeLog(_context: LogEventContexts, log: Partial<LogModel>, ...paths: string[]) {
    const [scope, , , group] = paths;

    WriteApiService.writeLog(scope, {
      context: log.context as 'user' | 'contact' | 'exclusion',
      event: log.event as string,
      group,
      ...(log.detail === undefined ? {} : { detail: log.detail }),
    });
  }

  static logIn(user: string, collectionPathBase: string[]): void {
    LogRepository.writeLog(
      LogEventContexts.user,
      {
        context: LogEventContexts.user,
        event: LogEvents.login,
        user,
      },
      ...LogRepository.getDocumentPath(LogEventContexts.user, user, collectionPathBase)
    );
  }

  static validateAccess(user: string, collectionPathBase: string[]): void {
    LogRepository.writeLog(
      LogEventContexts.user,
      {
        context: LogEventContexts.user,
        event: LogEvents.validateAccess,
        user,
      },
      ...LogRepository.getDocumentPath(LogEventContexts.user, user, collectionPathBase)
    );
  }

  static logOut(user: string, collectionPathBase: string[]): void {
    LogRepository.writeLog(
      LogEventContexts.user,
      {
        context: LogEventContexts.user,
        event: LogEvents.logout,
        user,
      },
      ...LogRepository.getDocumentPath(LogEventContexts.user, user, collectionPathBase)
    );
  }

  static registerExclusion(user: string, id: number, collectionPathBase: string[]): void {
    LogRepository.writeLog(
      LogEventContexts.exclusion,
      {
        context: LogEventContexts.exclusion,
        event: LogEvents.registerExclusion,
        user,
        detail: { id },
      },
      ...LogRepository.getDocumentPath(LogEventContexts.exclusion, IdHelper.formatExclusionId(id), collectionPathBase)
    );

    LogRepository.writeLog(
      LogEventContexts.user,
      {
        context: LogEventContexts.exclusion,
        event: LogEvents.registerExclusion,
        user,
        detail: { id },
      },
      ...LogRepository.getDocumentPath(LogEventContexts.user, user, collectionPathBase)
    );
  }

  static sendMessage(
    user: string,
    contactId: string,
    channel: Channel,
    type: TemplateType,
    collectNumber: number,
    collectionPathBase: string[]
  ): void {
    LogRepository.writeLog(
      LogEventContexts.contact,
      {
        context: LogEventContexts.contact,
        event: LogEvents.sendMessage,
        user,
        detail: { contactId, channel, type, collectNumber },
      },
      ...LogRepository.getDocumentPath(LogEventContexts.contact, contactId, collectionPathBase)
    );

    LogRepository.writeLog(
      LogEventContexts.user,
      {
        context: LogEventContexts.contact,
        event: LogEvents.sendMessage,
        user,
        detail: { contactId, channel, type, collectNumber },
      },
      ...LogRepository.getDocumentPath(LogEventContexts.user, user, collectionPathBase)
    );
  }

  static sendGratefulMessage(
    user: string,
    contactId: string,
    channel: Channel,
    collectNumber: number,
    collectionPathBase: string[]
  ): void {
    LogRepository.writeLog(
      LogEventContexts.contact,
      {
        context: LogEventContexts.contact,
        event: LogEvents.sendGratefulMessage,
        user,
        detail: { contactId, channel, collectNumber },
      },
      ...LogRepository.getDocumentPath(LogEventContexts.contact, contactId, collectionPathBase)
    );

    LogRepository.writeLog(
      LogEventContexts.user,
      {
        context: LogEventContexts.contact,
        event: LogEvents.sendGratefulMessage,
        user,
        detail: { contactId, channel, collectNumber },
      },
      ...LogRepository.getDocumentPath(LogEventContexts.user, user, collectionPathBase)
    );
  }

  static contactViewForms(contactId: number, collectNumber: number, collectionPathBase: string[]): void {
    LogRepository.writeLog(
      LogEventContexts.contact,
      {
        context: LogEventContexts.contact,
        event: LogEvents.viewForms,
        user: 'anonymous',
        detail: { contactId, collectNumber },
      },
      ...LogRepository.getDocumentPath(
        LogEventContexts.contact,
        IdHelper.formatContactId(contactId),
        collectionPathBase
      )
    );
  }

  static contactFinalizeForms(contactId: number, collectNumber: number, collectionPathBase: string[]): void {
    LogRepository.writeLog(
      LogEventContexts.contact,
      {
        context: LogEventContexts.contact,
        event: LogEvents.finalizeForms,
        user: 'anonymous',
        detail: { contactId, collectNumber },
      },
      ...LogRepository.getDocumentPath(
        LogEventContexts.contact,
        IdHelper.formatContactId(contactId),
        collectionPathBase
      )
    );
  }

  static contactSuccessfully(
    user: string,
    contactId: string,
    collectNumber: number,
    collectionPathBase: string[]
  ): void {
    LogRepository.writeLog(
      LogEventContexts.contact,
      {
        context: LogEventContexts.contact,
        event: LogEvents.successfullyContact,
        user,
        detail: { contactId, collectNumber },
      },
      ...LogRepository.getDocumentPath(LogEventContexts.contact, contactId, collectionPathBase)
    );

    LogRepository.writeLog(
      LogEventContexts.user,
      {
        context: LogEventContexts.contact,
        event: LogEvents.successfullyContact,
        user,
        detail: { contactId, collectNumber },
      },
      ...LogRepository.getDocumentPath(LogEventContexts.user, user, collectionPathBase)
    );
  }
}

export { LogRepository };
